import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import {
	PageTreeMacroChildrenQuery,
	type PageTreeMacroChildrenQuery$data,
	type PageTreeMacroChildrenQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroChildrenQuery.ts';

import {
	PageTreeMacroAncestorsQuery,
	type PageTreeMacroAncestorsQuery$data,
	type PageTreeMacroAncestorsQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroAncestorsQuery.ts';

import {
	PageTreeMacroSpaceHomepageQuery,
	type PageTreeMacroSpaceHomepageQuery$data,
	type PageTreeMacroSpaceHomepageQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroSpaceHomepageQuery.ts';

import {
	PageTreeMacroRootLevelQuery,
	type PageTreeMacroRootLevelQuery$data,
	type PageTreeMacroRootLevelQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroRootLevelQuery.ts';

import {
	PageTreeMacroSearchByTitleQuery,
	type PageTreeMacroSearchByTitleQuery$data,
	type PageTreeMacroSearchByTitleQuery$variables,
} from '@confluence/pagetree-macro/entry-points/PageTreeMacroSearchByTitleQuery.ts';

import {
	parseMacroParams,
	parseRootType,
	parsePageTitle,
	buildTitleSearchCql,
	resolveRootId,
	needsAncestorExpansion,
	needsRootChildren,
	CHILDREN_PAGE_SIZE,
} from '@confluence/pagetree-macro/entry-points/pageTreeUtils.ts';

import type {
	MacroContentNode,
	MacroPreloadHandler,
	MacroPreloadContext,
} from '../preloadMacroData.ts';

/**
 * Preload handler for the pagetree macro.
 *
 * Preloads the initial queries needed to render the tree:
 * - Root resolution (space homepage, ancestors for @parent)
 * - Root's children (first page)
 * - Ancestor IDs (for auto-expansion, capped at MAX_ANCESTOR_PRELOAD_DEPTH)
 *
 * NOTE: Excerpt preloading is intentionally omitted. The macro preloader
 * runs without a query boundary (getQueryBoundary() returns null). Queries
 * only succeed because GLOBAL_APOLLO_SKIP_QUERY is still unset when they
 * fire. Once setStopGraphQLQueries(true) is called before rendering, any
 * query that hasn't been dispatched yet is blocked. Queries chained via
 * .then() (like excerpts, which need child IDs from the children response)
 * race against this flag and lose — they fire after it's set to true.
 * The renderer fetches excerpts on the client instead.
 *
 * This populates the Apollo cache during SSR so the client
 * hydrates from cache instead of making new network requests.
 */
export const pagetreePreloadHandler: MacroPreloadHandler = {
	extensionKey: 'pagetree',

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		const { rootParam, spaceKey } = parseMacroParams(
			node.attrs?.parameters?.macroParams,
			context.spaceKey,
		);
		const rootType = parseRootType(rootParam);

		if (!spaceKey) {
			return;
		}

		// Step 1: Resolve root via the appropriate query
		let homepageData: PageTreeMacroSpaceHomepageQuery$data | null | undefined;
		let ancestorData: PageTreeMacroAncestorsQuery$data | null | undefined;
		let searchData: PageTreeMacroSearchByTitleQuery$data | null | undefined;

		switch (rootType) {
			case 'self':
				// No resolution query needed — rootId is contentId
				break;
			case 'title': {
				if (rootParam) {
					const pageTitle = parsePageTitle(rootParam);
					const cql = buildTitleSearchCql(pageTitle, spaceKey);
					const searchResult = await query<
						PageTreeMacroSearchByTitleQuery$data,
						PageTreeMacroSearchByTitleQuery$variables
					>({
						query: PageTreeMacroSearchByTitleQuery,
						variables: { cql },
						context: { single: true },
					});
					searchData = searchResult?.data;
				}
				break;
			}
			case 'parent': {
				const ancestorResult = await preloadAncestors(context.contentId);
				ancestorData = ancestorResult?.data;
				break;
			}
			case 'home': {
				const homepageResult = await query<
					PageTreeMacroSpaceHomepageQuery$data,
					PageTreeMacroSpaceHomepageQuery$variables
				>({
					query: PageTreeMacroSpaceHomepageQuery,
					variables: { spaceKey },
					context: { single: true },
				});
				homepageData = homepageResult?.data;
				break;
			}
			case 'none':
				await query<PageTreeMacroRootLevelQuery$data, PageTreeMacroRootLevelQuery$variables>({
					query: PageTreeMacroRootLevelQuery,
					variables: { spaceKey },
					context: { single: true },
				});
				break;
		}

		// Derive rootId and preload its children
		const rootId = resolveRootId({
			rootType,
			contentId: context.contentId,
			homepageData,
			ancestorData,
			searchData,
		});

		// Step 2: Fetch children + ancestors in parallel.
		// Children needs rootId, ancestors needs context.contentId — independent.
		const allTasks: Promise<any>[] = [];

		if (rootId && needsRootChildren(rootType)) {
			allTasks.push(preloadChildren(rootId));
		}

		if (needsAncestorExpansion(rootType)) {
			allTasks.push(preloadAncestors(context.contentId));
		}

		if (allTasks.length > 0) {
			await Promise.all(allTasks);
		}
	},
};

/** Preload children for a page */
const preloadChildren = (contentId: string) =>
	query<PageTreeMacroChildrenQuery$data, PageTreeMacroChildrenQuery$variables>({
		query: PageTreeMacroChildrenQuery,
		variables: { contentId, first: CHILDREN_PAGE_SIZE, after: null },
		context: { single: true },
	});

/** Preload ancestor IDs for a page */
const preloadAncestors = (contentId: string) =>
	query<PageTreeMacroAncestorsQuery$data, PageTreeMacroAncestorsQuery$variables>({
		query: PageTreeMacroAncestorsQuery,
		variables: { contentId },
		context: { single: true },
	});
